/**
 * Who may **enter** the automated Cloudflare beta (PRE-3294 / R1).
 *
 * Split out of `cf-beta-cohort.ts` so the dashboard can import it. That module
 * needs `node:crypto` for the bucket hash, which puts all of it out of reach of
 * a browser bundle and left the dashboard carrying a second copy of this rule —
 * the exact thing the rule's own comment warns about. Everything here is pure
 * string work, so it is exported from the browser entry point as well and the
 * two sides read one function.
 *
 * The cohort half — `cfBetaArm` and the bucket hash — stays where it was. It
 * runs only at registration, on the server, and has no business in a bundle.
 */

/**
 * The feature that opens the automated flow. Granted per user at registration
 * (`users.available_features`), so it is immediate and needs no plan change.
 */
export const CF_BETA_FEATURE = 'cloudflare-beta';

/**
 * The kill switch, in the shape of the AI Insights one: where this feature is
 * present, the beta is closed — for @prerender.io staff too. Set on a plan it
 * closes the beta for everyone (up to the one-hour features_for_plans cache);
 * set on a user it closes it for that account at once.
 *
 * It stops **new entries only**. A flow already under way keeps its screens, or
 * the customer is left with a half-changed Cloudflare zone and no way to
 * finish or reverse it.
 */
export const CF_BETA_BLOCK_FEATURE = 'cloudflare-beta-block';

/** Staff are always in the beta, in addition to the rolled-out share. */
export const PRERENDER_EMAIL_SUFFIX = '@prerender.io';

export type CfBetaAccessInput = {
  email: string | null | undefined;
  features: readonly string[] | null | undefined;
};

/**
 * Whether this account may enter the automated flow.
 *
 * One rule, shared by the dashboard and by rest-api's guard: a gate that only
 * the frontend enforces is not a gate, and a second copy of the rule is a
 * second thing to get wrong.
 */
export function canEnterCfBeta({ email, features }: CfBetaAccessInput): boolean {
  const granted = features ?? [];
  if (granted.includes(CF_BETA_BLOCK_FEATURE)) return false;
  if (email?.toLowerCase().endsWith(PRERENDER_EMAIL_SUFFIX)) return true;

  // Deliberately **not** falling back to `cfBetaArm` here. The arm is written
  // once, at registration, and the cohort is new signups only: computing it at
  // read time would enrol every older account the split puts in the treatment
  // arm into a flow that changes live DNS.
  return granted.includes(CF_BETA_FEATURE);
}

/**
 * True when the address is a Prerender staff address.
 *
 * Exported because a caller that needs to know *how* an account got in — the
 * dashboard reports `rollout` against `internal` — would otherwise re-implement
 * the suffix test, which is how the suffix and the rule drift apart.
 */
export function isPrerenderStaffEmail(email: string | null | undefined): boolean {
  return email?.toLowerCase().endsWith(PRERENDER_EMAIL_SUFFIX) ?? false;
}
